// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Analytics
// Charts and summary stats — placeholder for Phase 5
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, SimpleGrid, Stack, Group, Badge, Tooltip, Progress,
  Button, Collapse,
} from '../../components/gab/GabComponents';
import { IconDownload, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { haversineDistance } from '../../lib/geoopsGeo';
import { DEBRIS_TYPE_LABELS, DEBRIS_TYPE_COLORS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';
import { primary } from '../../lib/colors';

// ── Anomaly Panel with expandable flags + export ──
type Flag = { label: string; detail: string; severity: 'high' | 'medium' | 'low'; category: string; tickets: string[] };
const SEVERITY_COLORS: Record<string, string> = { high: 'red', medium: 'orange', low: 'yellow' };
const CATEGORY_COLORS: Record<string, string> = { Fraud: 'red', Compliance: 'blue' };

function AnomalyPanel({ flags, ticketCount }: { flags: Flag[]; ticketCount: number }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (i: number) => setExpanded(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });

  const exportCSV = () => {
    let csv = 'Severity,Category,Flag,Detail,Tickets\n';
    flags.forEach(f => {
      csv += `"${f.severity.toUpperCase()}","${f.category}","${f.label}","${f.detail.replace(/"/g, '""')}","${f.tickets.join('; ')}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `anomaly-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notifications.show({ message: `Exported ${flags.length} flags`, color: 'green' });
  };

  const highCount = flags.filter(f => f.severity === 'high').length;
  const medCount = flags.filter(f => f.severity === 'medium').length;

  if (flags.length === 0) return (
    <Paper p="md" radius="md" withBorder>
      <Text fw={700} mb="sm">Anomaly Detection</Text>
      <Badge color="green" variant="light" size="lg">No anomalies detected</Badge>
    </Paper>
  );

  return (
    <Paper p="md" radius="md" withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Text fw={700}>Anomaly Detection</Text>
          <Text size="xs" c="dimmed">Automated fraud, abuse, and compliance checks per FEMA guidelines</Text>
        </div>
        <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>
          Export Report
        </Button>
      </Group>
      <Group gap="md" mb="md">
        {highCount > 0 && <Badge color="red" size="sm">{highCount} HIGH</Badge>}
        {medCount > 0 && <Badge color="orange" size="sm">{medCount} MEDIUM</Badge>}
        <Text size="xs" c="dimmed">{flags.length} flags across {ticketCount} tickets</Text>
      </Group>
      <Stack gap={4}>
        {flags.map((f, i) => (
          <Paper key={i} p="xs" radius="sm" withBorder
            style={{ borderLeft: `3px solid var(--mantine-color-${SEVERITY_COLORS[f.severity]}-5)`, cursor: f.tickets.length > 0 ? 'pointer' : undefined }}
            onClick={() => f.tickets.length > 0 && toggle(i)}>
            <Group gap="sm" wrap="nowrap">
              <Badge size="xs" color={SEVERITY_COLORS[f.severity]} variant="filled" w={55}>{f.severity.toUpperCase()}</Badge>
              <Badge size="xs" color={CATEGORY_COLORS[f.category] ?? 'gray'} variant="light">{f.category}</Badge>
              <div style={{ flex: 1 }}>
                <Text size="sm" fw={500}>{f.label}</Text>
                <Text size="xs" c="dimmed">{f.detail}</Text>
              </div>
              {f.tickets.length > 0 && (
                <Group gap={4}>
                  <Badge size="xs" variant="light" color="gray">{f.tickets.length} ticket{f.tickets.length !== 1 ? 's' : ''}</Badge>
                  {expanded.has(i) ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                </Group>
              )}
            </Group>
            <Collapse in={expanded.has(i)}>
              <div style={{ paddingTop: 8, paddingLeft: 60 }}>
                <Group gap={4} style={{ flexWrap: 'wrap' }}>
                  {f.tickets.map(tn => (
                    <Badge key={tn} size="xs" variant="outline" color="blue"
                      style={{ cursor: 'pointer' }}
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/debris/tickets?search=${encodeURIComponent(tn)}`); }}>
                      {tn}
                    </Badge>
                  ))}
                </Group>
              </div>
            </Collapse>
          </Paper>
        ))}
      </Stack>
    </Paper>
  );
}

export default function DebrisAnalytics() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { zones, contractors, crews, trucks, disposalSites, loadZones, loadContractors, loadCrews, loadTrucks, loadDisposalSites } = useDebrisStore();
  const iTrucks = incidentId ? trucks : [];
  const { loadTickets, invoices, loadLoadTickets, loadInvoices } = useDebrisOpsStore();

  useEffect(() => {
    loadZones(); loadContractors(); loadCrews(); loadTrucks(); loadDisposalSites();
    loadLoadTickets(); loadInvoices();
  }, [loadZones, loadContractors, loadCrews, loadTrucks, loadDisposalSites, loadLoadTickets, loadInvoices]);

  const iTickets = incidentId ? loadTickets : [];
  const iZones = incidentId ? zones : [];
  const iCrews = incidentId ? crews : [];
  const iInvoices = incidentId ? invoices : [];

  // Compute CY for a ticket — fallback to loadPercentage × truck capacity if blank
  const getTicketCY = useCallback((t: { calculatedCubicYards: string; loadPercentage: string; truckId: string }) => {
    const stored = parseFloat(t.calculatedCubicYards);
    if (stored > 0) return stored;
    const loadPct = parseFloat(t.loadPercentage) || 0;
    const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return cap > 0 ? Math.round(cap * (loadPct / 100)) : 0;
  }, [iTrucks]);

  const totalCY = useMemo(() => iTickets.reduce((s, t) => s + getTicketCY(t), 0), [iTickets, getTicketCY]);
  const totalEstCY = useMemo(() => iZones.reduce((s, z) => s + (parseFloat(z.estimatedCubicYards) || 0), 0), [iZones]);
  const completionPct = totalEstCY > 0 ? Math.min((totalCY / totalEstCY) * 100, 100) : 0;

  const cyByType = useMemo(() => {
    const map: Record<string, number> = {};
    iTickets.forEach(t => { if (t.debrisType) map[t.debrisType] = (map[t.debrisType] || 0) + getTicketCY(t); });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [iTickets, getTicketCY]);

  const cyByZone = useMemo(() => {
    const map: Record<string, number> = {};
    iTickets.forEach(t => { if (t.zoneId) map[t.zoneId] = (map[t.zoneId] || 0) + getTicketCY(t); });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [iTickets, getTicketCY]);


  const totalInvoiced = useMemo(() => iInvoices.reduce((s, i) => s + (parseFloat(i.totalAmount) || 0), 0), [iInvoices]);

  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? `Zone ${id}`;

  return (
    <Stack gap="lg">
      <Title order={3} c="black">Debris Analytics</Title>

      {/* Summary row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total CY Hauled</Text>
          <Text fz={28} fw={800} c="#d97706">{totalCY.toLocaleString()}</Text>
          <Text size="xs" c="dimmed">of {totalEstCY.toLocaleString()} est.</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: `3px solid ${completionPct >= 80 ? '#16a34a' : primary[700]}` }}>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Completion</Text>
          <Text fz={28} fw={800} c={completionPct >= 80 ? '#16a34a' : primary[700]}>{completionPct.toFixed(1)}%</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-700)' }}>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Tickets</Text>
          <Text fz={28} fw={800}>{iTickets.length}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total Invoiced</Text>
          <Text fz={28} fw={800} c="#059669">
            ${totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Text>
          <Text size="xs" c="dimmed">{iInvoices.length} invoices</Text>
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* CY by type — Treemap */}
        <Paper p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Text fw={700}>Cubic Yards by Debris Type</Text>
            <Text size="sm" fw={600} c="#d97706">{totalCY.toLocaleString()} CY total</Text>
          </Group>
          {cyByType.length === 0 ? (
            <Text size="sm" c="dimmed">No data yet</Text>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', height: 200, gap: 2, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
                {cyByType.map(([type, cy]) => {
                  const pct = totalCY > 0 ? (cy / totalCY) * 100 : 0;
                  const color = DEBRIS_TYPE_COLORS[type as DebrisType] ?? '#6b7280';
                  const label = DEBRIS_TYPE_LABELS[type as DebrisType] ?? type;
                  return (
                    <Tooltip key={type} label={`${label}: ${cy.toLocaleString()} CY (${pct.toFixed(1)}%)`}>
                      <div style={{
                        flex: `${pct} 0 0%`,
                        minWidth: pct > 5 ? 60 : 24,
                        height: '100%',
                        background: color,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        cursor: 'default',
                      }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                      >
                        {pct > 8 && <Text size="xs" fw={700} c="white" ta="center" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{label}</Text>}
                        {pct > 5 && <Text size="xs" c="white" ta="center" style={{ opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{cy.toLocaleString()}</Text>}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <Stack gap={6}>
                {cyByType.map(([type, cy]) => {
                  const pct = totalCY > 0 ? (cy / totalCY) * 100 : 0;
                  const color = DEBRIS_TYPE_COLORS[type as DebrisType] ?? '#6b7280';
                  return (
                    <Group key={type} justify="space-between">
                      <Group gap="xs">
                        <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: color }} />
                        <Text size="sm" fw={500}>{DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}</Text>
                      </Group>
                      <Text size="sm" c="dimmed">{cy.toLocaleString()} CY ({pct.toFixed(1)}%)</Text>
                    </Group>
                  );
                })}
              </Stack>
            </>
          )}
        </Paper>

        {/* CY by zone */}
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Cubic Yards by Zone</Text>
          {cyByZone.length === 0 ? (
            <Text size="sm" c="dimmed">No data yet</Text>
          ) : (
            <Stack gap="xs">
              {cyByZone.map(([zoneId, cy]) => {
                const pct = totalCY > 0 ? (cy / totalCY) * 100 : 0;
                return (
                  <Group key={zoneId} gap="xs">
                    <Text size="sm" w={120} truncate>{zoneName(zoneId)}</Text>
                    <div style={{ flex: 1, height: 16, background: 'var(--mantine-color-gray-2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: primary[700], borderRadius: 4 }} />
                    </div>
                    <Text size="xs" w={80} ta="right">{cy.toLocaleString()} CY</Text>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* Resources summary */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="sm">Resources Summary</Text>
        <SimpleGrid cols={{ base: 2, sm: 5 }}>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Zones</Text>
            <Text fw={600}>{iZones.length} ({iZones.filter(z => z.status === 'active').length} active)</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Contractors</Text>
            <Text fw={600}>{(incidentId ? contractors : []).length}</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Crews</Text>
            <Text fw={600}>{iCrews.length} ({iCrews.filter(c => c.status === 'active').length} active)</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Trucks</Text>
            <Text fw={600}>{iTrucks.length} ({iTrucks.filter(t => t.status === 'certified').length} certified)</Text>
          </div>
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="dimmed">Disposal Sites</Text>
            <Text fw={600}>{(incidentId ? disposalSites : []).length} ({(incidentId ? disposalSites.filter(s => s.status === 'open') : []).length} open)</Text>
          </div>
        </SimpleGrid>
      </Paper>
      {/* CY by Contractor */}
      <Paper p="md" radius="md" withBorder>
        <Text fw={700} mb="sm">Cubic Yards by Contractor</Text>
        {(() => {
          const iCont = contractors;
          const contMap = Object.fromEntries(iCont.map(c => [String(c.id), c.companyName]));
          const byCont: Record<string, { name: string; cy: number; tickets: number }> = {};
          iTickets.forEach(t => {
            // Try direct contractorId on ticket first, then fall back to crew lookup
            let name = t.contractorId ? (contMap[t.contractorId] || null) : null;
            if (!name && t.crewId) {
              const crew = iCrews.find(c => String(c.id) === t.crewId);
              if (crew?.contractorId) name = contMap[crew.contractorId] || null;
            }
            name = name || 'Unknown';
            if (!byCont[name]) byCont[name] = { name, cy: 0, tickets: 0 };
            byCont[name].cy += getTicketCY(t);
            byCont[name].tickets++;
          });
          const sorted = Object.values(byCont).sort((a, b) => b.cy - a.cy);
          const maxCY = sorted.length > 0 ? sorted[0].cy : 1;
          return sorted.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center">No data</Text>
          ) : (
            <Stack gap="xs">
              {sorted.map(c => (
                <Group key={c.name} justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{c.name}</Text>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    <Progress value={(c.cy / maxCY) * 100} size="sm" w={120} color="blue" radius="xl" />
                    <Text size="xs" fw={600} w={80} ta="right">{c.cy.toLocaleString()} CY</Text>
                    <Badge size="xs" variant="light">{c.tickets} loads</Badge>
                  </Group>
                </Group>
              ))}
            </Stack>
          );
        })()}
      </Paper>

      {/* CY by Crew / Driver */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Top Crews by Volume</Text>
          {(() => {
            const byCrew: Record<string, { name: string; cy: number; loads: number }> = {};
            iTickets.forEach(t => {
              const crew = t.crewId ? iCrews.find(c => String(c.id) === t.crewId) : null;
              const name = crew?.crewName || t.truckNumber || `Ticket ${t.ticketNumber}`;
              if (!byCrew[name]) byCrew[name] = { name, cy: 0, loads: 0 };
              byCrew[name].cy += getTicketCY(t);
              byCrew[name].loads++;
            });
            return Object.values(byCrew).sort((a, b) => b.cy - a.cy).slice(0, 10).map(c => (
              <Group key={c.name} justify="space-between" mb={4}>
                <Text size="sm" truncate style={{ flex: 1 }}>{c.name}</Text>
                <Group gap="xs">
                  <Text size="xs" fw={600}>{c.cy.toLocaleString()} CY</Text>
                  <Badge size="xs" variant="light">{c.loads}</Badge>
                </Group>
              </Group>
            ));
          })()}
        </Paper>

        <Paper p="md" radius="md" withBorder>
          <Text fw={700} mb="sm">Top Drivers by Loads</Text>
          {(() => {
            const byDriver: Record<string, { name: string; loads: number; cy: number }> = {};
            iTickets.forEach(t => {
              const crew = t.crewId ? iCrews.find(c => String(c.id) === t.crewId) : null;
              const name = crew?.driverName || t.monitorName || 'Unknown';
              if (!byDriver[name]) byDriver[name] = { name, loads: 0, cy: 0 };
              byDriver[name].loads++;
              byDriver[name].cy += getTicketCY(t);
            });
            return Object.values(byDriver).sort((a, b) => b.loads - a.loads).slice(0, 10).map(d => (
              <Group key={d.name} justify="space-between" mb={4}>
                <Text size="sm" truncate style={{ flex: 1 }}>{d.name}</Text>
                <Group gap="xs">
                  <Badge size="xs" variant="light">{d.loads} loads</Badge>
                  <Text size="xs" c="dimmed">{d.cy.toLocaleString()} CY</Text>
                </Group>
              </Group>
            ));
          })()}
        </Paper>
      </SimpleGrid>

      {/* Anomaly Detection */}
      {(() => {
        type Flag = { label: string; detail: string; severity: 'high' | 'medium' | 'low'; category: string; tickets: string[] };
        const flags: Flag[] = [];
        const iSites = incidentId ? disposalSites : [];

        // Run all checks with ticket references

        // ── 1. High load count per driver ──
        const driverLoads: Record<string, { count: number; tickets: string[] }> = {};
        iTickets.forEach(t => {
          const crew = t.crewId ? iCrews.find(c => String(c.id) === t.crewId) : null;
          const name = crew?.driverName || 'Unknown';
          if (name === 'Unknown') return;
          if (!driverLoads[name]) driverLoads[name] = { count: 0, tickets: [] };
          driverLoads[name].count++;
          driverLoads[name].tickets.push(t.ticketNumber);
        });
        const avgLoads = Object.values(driverLoads).length > 0
          ? Object.values(driverLoads).reduce((a, b) => a + b.count, 0) / Object.keys(driverLoads).length : 0;
        Object.entries(driverLoads).forEach(([name, d]) => {
          if (d.count > avgLoads * 2.5 && d.count > 5)
            flags.push({ label: `High load count: ${name}`, detail: `${d.count} loads (avg: ${avgLoads.toFixed(0)})`, severity: 'medium', category: 'Fraud', tickets: d.tickets });
        });

        // ── 2. Unusually high CY ──
        const cyValues = iTickets.map(t => getTicketCY(t)).filter(cy => cy > 0);
        const avgCY = cyValues.length > 0 ? cyValues.reduce((a, b) => a + b, 0) / cyValues.length : 0;
        iTickets.forEach(t => {
          const cy = getTicketCY(t);
          if (cy > avgCY * 3 && cy > 50)
            flags.push({ label: `High CY: ${t.ticketNumber}`, detail: `${cy} CY (avg: ${avgCY.toFixed(0)})`, severity: 'high', category: 'Fraud', tickets: [t.ticketNumber] });
        });

        // ── 3. CY exceeds truck capacity ──
        iTickets.forEach(t => {
          const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
          if (!truck) return;
          const cap = parseFloat(truck.cubicYardCapacity || '0');
          const cy = getTicketCY(t);
          if (cap > 0 && cy > cap * 1.05)
            flags.push({ label: `Over capacity: ${t.ticketNumber}`, detail: `${cy} CY on ${truck.truckNumber} (capacity: ${cap} CY)`, severity: 'high', category: 'Fraud', tickets: [t.ticketNumber] });
        });

        // ── 4. Loading vs tower discrepancy >15pt ──
        iTickets.forEach(t => {
          const loading = parseFloat(t.loadingLoadPct || '0');
          const tower = parseFloat(t.towerLoadPct || '0');
          if (loading > 0 && tower > 0 && Math.abs(loading - tower) > 15)
            flags.push({ label: `Estimate discrepancy: ${t.ticketNumber}`, detail: `Loading: ${loading}%, Tower: ${tower}% (${Math.abs(loading - tower).toFixed(0)}pt diff)`, severity: 'medium', category: 'Compliance', tickets: [t.ticketNumber] });
        });

        // ── 5. No GPS ──
        const noGps = iTickets.filter(t => !parseFloat(t.pickupLat) && !parseFloat(t.pickupLng) && t.status !== 'draft');
        if (noGps.length > 0)
          flags.push({ label: `Tickets without GPS`, detail: `${noGps.length} ticket${noGps.length > 1 ? 's' : ''} have no pickup coordinates`, severity: 'medium', category: 'Compliance', tickets: noGps.map(t => t.ticketNumber) });

        // ── 6. Verified without tower signature ──
        const noSig = iTickets.filter(t => (t.status === 'verified' || t.status === 'delivered') && !t.monitorSignature);
        if (noSig.length > 0)
          flags.push({ label: `Verified without tower signature`, detail: `${noSig.length} ticket${noSig.length > 1 ? 's' : ''}`, severity: 'high', category: 'Compliance', tickets: noSig.map(t => t.ticketNumber) });

        // ── 7. Missing loading monitor ──
        const noLoadMon = iTickets.filter(t => t.status !== 'draft' && !t.loadingMonitorName);
        if (noLoadMon.length > 0)
          flags.push({ label: `Missing loading monitor`, detail: `${noLoadMon.length} ticket${noLoadMon.length > 1 ? 's' : ''}`, severity: 'medium', category: 'Compliance', tickets: noLoadMon.map(t => t.ticketNumber) });

        // ── 8. Rapid turnaround (same truck <20 min) ──
        const truckTix: Record<string, typeof iTickets> = {};
        iTickets.forEach(t => { if (t.truckId && t.pickupTime) { if (!truckTix[t.truckId]) truckTix[t.truckId] = []; truckTix[t.truckId].push(t); } });
        Object.entries(truckTix).forEach(([truckId, tix]) => {
          if (tix.length < 2) return;
          const sorted = [...tix].sort((a, b) => (a.pickupTime || '').localeCompare(b.pickupTime || ''));
          for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1]; const curr = sorted[i];
            if (!prev.pickupTime || !curr.pickupTime) continue;
            const diffMin = (new Date(curr.pickupTime).getTime() - new Date(prev.pickupTime).getTime()) / 60000;
            if (diffMin >= 0 && diffMin < 20) {
              const truck = iTrucks.find(tr => String(tr.id) === truckId);
              flags.push({ label: `Rapid turnaround: ${truck?.truckNumber || truckId}`, detail: `${prev.ticketNumber} → ${curr.ticketNumber} only ${diffMin.toFixed(0)} min apart`, severity: 'high', category: 'Fraud', tickets: [prev.ticketNumber, curr.ticketNumber] });
            }
          }
        });

        // ── 9. Expired trucks ──
        iTickets.forEach(t => {
          if (t.status === 'draft' || t.status === 'voided') return;
          const truck = iTrucks.find(tr => String(tr.id) === t.truckId);
          if (!truck) return;
          const expired = truck.certExpiresAt && new Date(truck.certExpiresAt) < new Date();
          const cs = truck.certStatus || truck.status;
          if (expired || cs === 'expired' || cs === 'decertified')
            flags.push({ label: `Expired truck: ${truck.truckNumber}`, detail: `Ticket ${t.ticketNumber} — ${cs || 'expired'}${truck.certExpiresAt ? ` (${truck.certExpiresAt})` : ''}`, severity: 'high', category: 'Compliance', tickets: [t.ticketNumber] });
        });

        // ── 10. Impossibly fast trip ──
        iTickets.forEach(t => {
          if (!t.pickupTime || !t.disposalTime || !t.disposalSiteId) return;
          const pLat = parseFloat(t.pickupLat); const pLng = parseFloat(t.pickupLng);
          const site = iSites.find(s => String(s.id) === t.disposalSiteId);
          if (!site || !pLat || !pLng) return;
          const sLat = parseFloat(site.latitude); const sLng = parseFloat(site.longitude);
          if (!sLat || !sLng) return;
          const distKm = haversineDistance(pLat, pLng, sLat, sLng) / 1000;
          const tripMin = (new Date(t.disposalTime).getTime() - new Date(t.pickupTime).getTime()) / 60000;
          const minMin = distKm + 10;
          if (tripMin > 0 && tripMin < minMin * 0.5 && tripMin < 30)
            flags.push({ label: `Impossibly fast trip: ${t.ticketNumber}`, detail: `${distKm.toFixed(1)} km in ${tripMin.toFixed(0)} min (min ~${minMin.toFixed(0)} min)`, severity: 'high', category: 'Fraud', tickets: [t.ticketNumber] });
        });

        // ── 11. Hand-loaded over 50% ──
        iTickets.forEach(t => {
          if (t.handLoaded !== 'true') return;
          const pct = parseFloat(t.loadPercentage || t.towerLoadPct || '0');
          if (pct > 50)
            flags.push({ label: `Hand-loaded over 50%: ${t.ticketNumber}`, detail: `Claims ${pct}% — FEMA cap is 50%`, severity: 'medium', category: 'Compliance', tickets: [t.ticketNumber] });
        });

        // Sort high first
        flags.sort((a, b) => ({ high: 0, medium: 1, low: 2 }[a.severity] - { high: 0, medium: 1, low: 2 }[b.severity]));

        return <AnomalyPanel flags={flags} ticketCount={iTickets.length} />;
      })()}
    </Stack>
  );
}
